export type DetailTab = 'details' | 'form';

export interface CatalogParams {
  q: string;
  categoryId: number | null;
  formId: number | null;
  tab: DetailTab;
  page: number | null;
}

const ID = /^[1-9]\d{0,15}$/;
const KEYS = ['q', 'category', 'form', 'tab', 'page'] as const;
const Q_MAX = 100;

export type CatalogParamKey = (typeof KEYS)[number];

/** Reads `/start`'s search params. `invalid` is true when a present param failed validation. */
export function readCatalogParams(sp: URLSearchParams): CatalogParams & { invalid: boolean } {
  let invalid = false;
  let q = sp.get('q') ?? '';
  if (q.length > Q_MAX) {
    q = q.slice(0, Q_MAX);
    invalid = true;
  }

  const categoryId = readId(sp.get('category'));
  if (sp.has('category') && categoryId === null) invalid = true;

  const formId = readId(sp.get('form'));
  if (sp.has('form') && formId === null) invalid = true;

  const tabRaw = sp.get('tab');
  let tab: DetailTab = 'details';
  if (tabRaw !== null) {
    if (tabRaw === 'form') tab = 'form';
    else invalid = true;
  }

  let page = readId(sp.get('page'));
  if (sp.has('page') && (page === null || page === 1 || tab !== 'form')) {
    page = null;
    invalid = true;
  }

  return { q, categoryId, formId, tab, page, invalid };
}

/**
 * A copy of `sp` with `changes` applied. `null` or `''` deletes the key.
 * Key order is stable: q, category, form, tab, page. Other keys are dropped.
 */
export function withParams(
  sp: URLSearchParams,
  changes: Partial<Record<CatalogParamKey, string | number | null>>,
): URLSearchParams {
  const next = new URLSearchParams(sp);
  for (const key of KEYS) {
    if (!Object.hasOwn(changes, key)) continue;
    const value = changes[key];
    if (value === null || value === '') next.delete(key);
    else next.set(key, String(value));
  }
  const ordered = new URLSearchParams();
  for (const key of KEYS) {
    const value = next.get(key);
    if (value !== null) ordered.set(key, value);
  }
  return ordered;
}

/** `to.search` for a router link. Empty params stay an empty search. */
export function toSearch(params: URLSearchParams): string {
  const value = params.toString();
  return value.length === 0 ? '' : `?${value}`;
}

function readId(raw: string | null): number | null {
  if (raw === null || !ID.test(raw)) return null;
  return Number(raw);
}
